/**
 * Copy for a failed API call, written after a "Couldn’t …" lead: why it
 * failed and what to do next (book §14: what happened + why + what to do).
 */

/**
 * The backend's own explanation (`{ success: false, error: '…' }` on an RTK
 * Query rejection), ending in a period. Null when the request never got an
 * answer or the answer carried no message.
 */
export const apiErrorMessage = (err: unknown): string | null => {
  const data = (err as { data?: { error?: unknown } } | null)?.data;
  const raw = typeof data?.error === 'string' ? data.error.trim() : '';
  if (!raw) return null;
  return /[.!?]$/.test(raw) ? raw : `${raw}.`;
};

/**
 * Why a call failed and what to do. A refusal (4xx) shows the API's reason,
 * which says what to fix ("Only HR or an admin can override an active VP
 * review."). The API's 5xx messages only restate the failure ("Failed to
 * update time off entry"), so a server error or no answer at all gets `retry`.
 */
export const failureReason = (err: unknown, retry = 'Try again in a minute.') => {
  const status = (err as { status?: unknown } | null | undefined)?.status;
  if (status === 401) return 'Your session has expired. Sign in again to continue.';
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return apiErrorMessage(err) ?? `The server didn’t accept it. ${retry}`;
  }
  if ((typeof status === 'number' && status >= 500) || status === 'PARSING_ERROR') {
    return `The server ran into a problem. ${retry}`;
  }
  return `The server didn’t respond. ${retry}`;
};
